import type {
  SnippetData,
  SnippetVersion,
  SnippetVersionProps,
} from 'flow/service/snippets/types';

import type { WorkflowProps } from '~/dashboard/views/workflows/store/engine/types';
import type { API } from '~/helpers/api-new/modules';
import getLocalSnippetWorkflowTransformedData from './getLocalSnippetWorkflowTransformedData';
import { createSnippetVersion } from 'flow/service/snippets';

interface GetLocalSnippetDataOptions {
  // flag to opt out of regenerating public ids for the snippet data
  preservePublicIds?: boolean;
  snippetToken: string;
  typhonDomain: string;
  versionId: number;
  workflowProps: WorkflowProps;
  libraryApi: API['library'];
}

export interface GetLocalSnippetDataResult {
  snippetVersionData: Omit<SnippetData, 'name' | 'token' | 'workflowType'>;
  snippetVersionProps: SnippetVersionProps;
}

/**
 * Fetches and transforms local snippet data from legacy to JSON schema format.
 * By default, it regenerates public ids to create a ready-to-use snippet.
 */
export default async function getLocalSnippetVersion({
  preservePublicIds = false,
  snippetToken,
  typhonDomain,
  versionId,
  workflowProps,
  libraryApi,
}: GetLocalSnippetDataOptions): Promise<SnippetVersion> {
  const {
    extraResponseData,
    transformedData,
  } = await getLocalSnippetWorkflowTransformedData({
    snippetToken,
    typhonDomain,
    versionId,
    workflowProps,
    preservePublicIds,
  });

  // this call is needed as the existing endpoint to get wf data (for snippet wfs) does not return the snippet metadata
  const snippetResponse = await libraryApi.getLocalSnippet(snippetToken);

  return createSnippetVersion({
    actions: transformedData.actions,
    thresholds: transformedData.thresholds,
    dataRoutes: transformedData.dataRoutes,
    tags: workflowProps.tags,
    outcomes: workflowProps.outcomes,
    services: Object.values(workflowProps.services),
    publishedAttributes: extraResponseData.publishedAttributes,
    reasonCodeGroups: workflowProps.reasonCodeGroups,
    lists: workflowProps.lists ?? [],
    metadata: {
      token: snippetResponse.snippet_metadata.token,
      version: versionId,
    },
    snippets: Object.values(workflowProps.snippets),
  });
}
